/**
 * SourceChip / StatusPill — small dashboard helpers.
 *
 * Mirrors design_reference/js/screens-dashboard.jsx `SourceChip` (web→info,
 * mobile→accent, uploaded→neutral) and the inline active/archived status pill.
 */

import { Pill, type PillKind } from './ui/pill';

/** project metadata.source → V2 pill kind. */
const SOURCE_KIND: Record<string, PillKind> = {
  web: 'info',
  mobile: 'accent',
  uploaded: 'neutral',
};

/** Props for {@link SourceChip}. */
export interface SourceChipProps {
  /** The project source (`metadata.source`). */
  source?: string | null;
}

/** Renders a capitalized source pill (web/mobile/uploaded). */
export function SourceChip({ source }: SourceChipProps) {
  if (!source) return <span className="text-[var(--tx-faint)]">—</span>;
  return (
    <Pill kind={SOURCE_KIND[source] ?? 'neutral'} className="capitalize">
      {source}
    </Pill>
  );
}

/** Props for {@link StatusPill}. */
export interface StatusPillProps {
  /** The project status string ('active' | 'archived' | null). */
  status?: string | null;
}

/** Renders an Active (ok dot) / Archived (neutral dot) status pill. */
export function StatusPill({ status }: StatusPillProps) {
  if (status === 'archived') {
    return (
      <Pill kind="neutral" dot="var(--tx-faint)">
        Archived
      </Pill>
    );
  }
  return (
    <Pill kind="ok" dot="var(--ok)">
      Active
    </Pill>
  );
}
